import { notify } from '@/lib/toast';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { Users, Plus, Mail, Phone, UserCheck, UserX, FlaskConical, Pencil, Tag, Trash2, FileText, MessageCircle, Share2, Download, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { StatCard } from '@/components/ui/stat-card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth.store';
import { validateNTN, validateCNIC, formatCurrency, formatDate, toWhatsAppNumber } from '@/lib/utils';
import api, { openStoredObject } from '@/lib/api';
import { PageHeader } from '@/components/ui/page-header';
import { CustomerCsvImport } from './CustomerCsvImport';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { SearchInput } from '@/components/ui/search-input';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';

const TAG_COLORS = [
  'bg-primary/10 text-primary-text',
  'bg-primary/10 text-primary-text',
  'bg-success/10 text-success-text',
  'bg-warning/10 text-warning-text',
  'bg-destructive/10 text-destructive-text',
  'bg-info/10 text-info-text',
];
function tagColor(tag: string) { return TAG_COLORS[Math.abs(tag.charCodeAt(0) * 7 + tag.charCodeAt(1 % tag.length)) % TAG_COLORS.length]; }

const emptyForm = { name: '', phone: '', email: '', address: '', city: '', ntn: '', cnic: '', notes: '', isOnAtl: false as boolean, creditLimit: undefined as number | undefined, tagsInput: '', priceTierId: '' as string | undefined, marketingSmsOptIn: false as boolean, marketingEmailOptIn: false as boolean, portalEnabled: true as boolean, referredByCode: '',
  // MEIMS SRS Module 1 — extended personal / military / employment information
  fatherName: '', dateOfBirth: '', gender: '', alternatePhone: '', province: '',
  armyNumber: '', rank: '', unit: '', brigade: '', postingStation: '', serviceStatus: '', serviceYears: undefined as number | undefined,
  employerName: '', department: '', monthlySalary: undefined as number | undefined, employmentDuration: '' };

/** Customer row as returned by `GET /customers` (fields this page reads). */
interface CustomerRecord {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  ntn?: string | null;
  cnic?: string | null;
  notes?: string | null;
  isOnAtl?: boolean | null;
  creditLimit?: number | string | null;
  outstandingBalance?: number | string | null;
  creditBlocked?: boolean | null;
  tags?: string[] | string | null;
  priceTierId?: string | null;
  priceTier?: { name: string } | null;
  loyaltyPoints?: number;
  referralCode?: string | null;
  marketingSmsOptIn?: boolean | null;
  marketingEmailOptIn?: boolean | null;
  // POS-I106
  portalEnabled?: boolean | null;
  portalLastLoginAt?: string | null;
  portalLinkSentAt?: string | null;
  fatherName?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  alternatePhone?: string | null;
  province?: string | null;
  armyNumber?: string | null;
  rank?: string | null;
  unit?: string | null;
  brigade?: string | null;
  postingStation?: string | null;
  serviceStatus?: string | null;
  serviceYears?: number | string | null;
  employerName?: string | null;
  department?: string | null;
  monthlySalary?: number | string | null;
  employmentDuration?: string | null;
}

/** Paginated list envelope from `GET /customers`. */
interface CustomersResponse {
  data: CustomerRecord[];
  total?: number;
  meta?: { total?: number };
}

/** Body sent to `POST /customers` / `PATCH /customers/:id`. */
function isDuplicatePhone(err: unknown): boolean {
  const r = (err as { response?: { status?: number; data?: { code?: string } } })?.response;
  return r?.status === 409 && r?.data?.code === 'DUPLICATE_PHONE';
}
function duplicatePhoneMessage(err: unknown): string {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';
}

type CustomerPayload = Omit<typeof emptyForm, 'tagsInput' | 'referredByCode' | 'priceTierId'> & {
  tags: string[];
  priceTierId: string | null;
  referredByCode?: string;
};

/** Price tier row from `GET /price-tiers`. */
interface PriceTierOption {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

/** Customer / guarantor document row. */
interface DocumentRecord {
  id: string;
  documentType: string;
  fileUrl: string;
  fileName?: string | null;
}

/** Guarantor row from `GET /customers/:id/guarantors`. */
interface GuarantorRecord {
  id: string;
  name: string;
  fatherName?: string | null;
  cnic?: string | null;
  phone?: string | null;
  alternatePhone?: string | null;
  address?: string | null;
  relationship?: string | null;
  notes?: string | null;
}

export function CustomersPage() {
  const { t } = useTranslation('customers');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [tagFilter, setTagFilter] = useState('');
  const { hasPermission, tenant } = useAuthStore();
  const canWrite = hasPermission('customers:write') || hasPermission('customers:*');
  const canDelete = hasPermission('customers:delete') || hasPermission('customers:*');
  const canUseLoyalty = hasPermission('loyalty:*');
  const canUsePriceTiers = hasPermission('price-tiers:*') || hasPermission('price-tiers:read');
  const canViewFbr = hasPermission('fbr:*') || hasPermission('fbr:read');

  const queryClient = useQueryClient();
  const handleCustomerUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  }, [queryClient]);
  useSocketEvent('sale.created', handleCustomerUpdate);
  useSocketEvent('sale.voided', handleCustomerUpdate);
  useSocketEvent('sale.refunded', handleCustomerUpdate);
  useSocketEvent('customer.updated', handleCustomerUpdate);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  const { data: priceTiersData } = useQuery<PriceTierOption[]>({
    queryKey: ['price-tiers'],
    queryFn: () => api.get('/price-tiers').then((r) => r.data),
    staleTime: 60_000,
    enabled: canUsePriceTiers,
  });

  const saveMutation = useMutation({
    mutationFn: (data: CustomerPayload & { allowDuplicatePhone?: boolean }) => api.post('/customers', data),
    onSuccess: () => {
      toast.success(t('customerAdded'));
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDialogOpen(false);
    },
    onError: (err, vars) => {
      // POS-I104: a phone that already belongs to another customer asks for confirmation.
      if (isDuplicatePhone(err) && window.confirm(`${duplicatePhoneMessage(err)}

${t('saveAnyway')}`)) {
        saveMutation.mutate({ ...vars, allowDuplicatePhone: true });
        return;
      }
      notify.error(err, t('failedToAddCustomer'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CustomerPayload & { allowDuplicatePhone?: boolean }) => api.patch(`/customers/${editingId}`, data),
    onSuccess: () => {
      toast.success(t('customerUpdated'));
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer'] });
      setDialogOpen(false);
      setEditingId(null);
    },
    onError: (err, vars) => {
      if (isDuplicatePhone(err) && window.confirm(`${duplicatePhoneMessage(err)}

${t('saveAnyway')}`)) {
        updateMutation.mutate({ ...vars, allowDuplicatePhone: true });
        return;
      }
      notify.error(err, t('failedToUpdateCustomer'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${id}`),
    onSuccess: () => {
      toast.success(t('customerDeleted'));
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setConfirmDelete(null);
    },
    onError: (err) => notify.error(err, t('failedToDeleteCustomer')),
  });

  const clearCreditBlockMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/customers/${id}/clear-credit-block`),
    onSuccess: () => {
      toast.success(t('creditBlockCleared'));
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => notify.error(err, t('failedToClearCreditBlock')),
  });

  const openDialog = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (c: CustomerRecord) => {
    const existingTags: string[] = Array.isArray(c.tags) ? c.tags : (typeof c.tags === 'string' ? JSON.parse(c.tags || '[]') : []);
    setForm({
      name: c.name || '',
      phone: c.phone || '',
      email: c.email || '',
      address: c.address || '',
      city: c.city || '',
      ntn: c.ntn || '',
      cnic: c.cnic || '',
      notes: c.notes || '',
      isOnAtl: c.isOnAtl ?? false,
      creditLimit: c.creditLimit != null ? Number(c.creditLimit) : undefined,
      tagsInput: existingTags.join(', '),
      priceTierId: c.priceTierId ?? '',
      marketingSmsOptIn: c.marketingSmsOptIn ?? false,
      marketingEmailOptIn: c.marketingEmailOptIn ?? false,
      portalEnabled: c.portalEnabled ?? true,
      referredByCode: '',  // not editable after creation
      fatherName: c.fatherName || '',
      dateOfBirth: c.dateOfBirth ? String(c.dateOfBirth).slice(0, 10) : '',
      gender: c.gender || '',
      alternatePhone: c.alternatePhone || '',
      province: c.province || '',
      armyNumber: c.armyNumber || '',
      rank: c.rank || '',
      unit: c.unit || '',
      brigade: c.brigade || '',
      postingStation: c.postingStation || '',
      serviceStatus: c.serviceStatus || '',
      serviceYears: c.serviceYears != null ? Number(c.serviceYears) : undefined,
      employerName: c.employerName || '',
      department: c.department || '',
      monthlySalary: c.monthlySalary != null ? Number(c.monthlySalary) : undefined,
      employmentDuration: c.employmentDuration || '',
    });
    setEditingId(c.id);
    setDialogOpen(true);
  };

  // POS-I131: the customer page's Edit button links here with ?edit=<id>; open that customer's edit dialog once.
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam || !canWrite) return;
    let cancelled = false;
    api.get(`/customers/${editParam}`).then((r) => {
      if (cancelled) return;
      openEditDialog(r.data as CustomerRecord);
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('edit'); return n; }, { replace: true });
    }).catch(() => {
      if (!cancelled) setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('edit'); return n; }, { replace: true });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, canWrite]);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error(t('customerNameRequired'));
      return;
    }
    if (form.ntn && !validateNTN(form.ntn)) {
      toast.error(t('ntnValidation'));
      return;
    }
    if (form.cnic && !validateCNIC(form.cnic)) {
      toast.error(t('cnicValidation'));
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t('invalidEmail'));
      return;
    }
    const tags = form.tagsInput.split(',').map(t => t.trim()).filter(Boolean);
    const { tagsInput: _ti, referredByCode, ...rest } = form;
    const payload: CustomerPayload = { ...rest, tags, priceTierId: rest.priceTierId || null };
    if (editingId) {
      updateMutation.mutate(payload);
    } else {
      const createPayload: CustomerPayload = { ...payload };
      if (referredByCode.trim()) createPayload.referredByCode = referredByCode.trim().toUpperCase();
      saveMutation.mutate(createPayload);
    }
  };

  const isPending = saveMutation.isPending || updateMutation.isPending;

  const customerDialogFooter = (
    <div className="flex gap-3">
      <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
        {t('cancel', { ns: 'common' })}
      </Button>
      <Button
        type="submit"
        form="customer-form"
        variant="gradient"
        className="flex-1"
        disabled={isPending}
      >
        {isPending ? t('loading', { ns: 'common' }) : editingId ? t('updateCustomer') : t('saveCustomer')}
      </Button>
    </div>
  );

  const updateField = (field: string, value: string | boolean | number | undefined) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const { data, isLoading } = useQuery<CustomersResponse>({
    queryKey: ['customers', search, page, tagFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      if (tagFilter) params.set('tag', tagFilter);
      const res = await api.get(`/customers?${params}`);
      return res.data;
    },
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const res = await api.get(`/customers/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Customers exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('title')}
        subtitle={t('manageCustomers')}
        action={
          <div className="flex gap-2">
            {canWrite && <CustomerCsvImport />}
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
              <Download className="me-1.5 h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            {canWrite && (
              <Button variant="gradient" onClick={openDialog}>
                <Plus className="me-2 h-4 w-4" />
                {t('addCustomer')}
              </Button>
            )}
          </div>
        }
      />

      {isTestMode && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span><strong>{t('testModeLabel')}</strong> {t('testModeCustomersMessage')} <a href="/settings" className="underline">{t('testModeSettings')}</a> {t('testModeCustomersSuffix')}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-1">
        <StatCard
          className="animate-fade-in-up stagger-1"
          label={t('totalCustomers')}
          value={data?.total ?? data?.meta?.total ?? 0}
          icon={Users}
          accent="default"
        />
      </div>

      {/* Customer list */}
      <Card className="animate-fade-in-up stagger-3">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg">{t('allCustomers')}</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Tag filter */}
              <Dropdown
                value={tagFilter}
                onChange={(e) => { setTagFilter(e.value); setPage(1); }}
                options={[
                  { label: t('allTags'), value: '' },
                  ...Array.from(new Set(
                    (data?.data ?? []).flatMap((c: CustomerRecord) => Array.isArray(c.tags) ? c.tags : (typeof c.tags === 'string' ? JSON.parse(c.tags || '[]') : []))
                  )).filter(Boolean).sort().map((tag: string) => ({ label: tag, value: tag })),
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('allTags')}
                className="h-8 text-xs"
              />
              <SearchInput
                value={search}
                onChange={(value) => { setSearch(value); setPage(1); }}
                placeholder={t('searchPlaceholder')}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : !data?.data?.length ? (
            <EmptyState
              icon={Users}
              title={t('noCustomers')}
              description={t('noCustomersSubtitle')}
              action={canWrite ? { label: t('addCustomer'), onClick: () => setDialogOpen(true) } : undefined}
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {data.data.map((c: CustomerRecord) => {
                const tags: string[] = Array.isArray(c.tags) ? c.tags : (typeof c.tags === 'string' ? JSON.parse(c.tags || '[]') : []);
                return (
                <div
                  key={c.id}
                  className="flex items-start gap-3 rounded-xl border p-4 transition-all duration-200 hover:bg-accent/50 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/80 text-sm font-bold text-primary-foreground">
                    {c.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{c.name}</p>
                    {c.email && (
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Mail className="h-3 w-3" /> {c.email}
                      </p>
                    )}
                    {c.phone && (
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Phone className="h-3 w-3" /> {c.phone}
                      </p>
                    )}
                    {canViewFbr && (c.isOnAtl ? (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success-text">
                        <UserCheck className="h-2.5 w-2.5" /> {t('atlBadge')}
                      </span>
                    ) : (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-text">
                        <UserX className="h-2.5 w-2.5" /> {t('nonAtlBadge')}
                      </span>
                    ))}
                    {canUseLoyalty && (c.loyaltyPoints ?? 0) > 0 && (
                      <p className="mt-1 text-xs font-medium text-success-text">
                        {t('loyaltyPointsCount', { count: c.loyaltyPoints })}
                      </p>
                    )}
                    {canUsePriceTiers && c.priceTier && (
                      <p className="mt-1 text-xs font-medium text-primary-text">
                        📦 {c.priceTier.name}
                      </p>
                    )}
                    {c.creditLimit != null && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('creditLabel')} <span className="font-medium">{formatCurrency(Number(c.creditLimit))}</span>
                        {Number(c.outstandingBalance ?? 0) > 0 && (
                          <span className="text-destructive-text ms-1">({formatCurrency(Number(c.outstandingBalance))} {t('dueLabel')})</span>
                        )}
                      </p>
                    )}
                    {c.creditBlocked && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive-text px-2 py-0.5 text-xs font-medium">
                          <Ban className="h-3 w-3" />
                          {t('creditBlockedBadge')}
                        </span>
                        {canWrite && (
                          <button
                            onClick={() => clearCreditBlockMutation.mutate(c.id)}
                            disabled={clearCreditBlockMutation.isPending}
                            className="text-xs text-primary-text hover:underline disabled:opacity-50"
                          >
                            {t('clearCreditBlock')}
                          </button>
                        )}
                      </div>
                    )}
                    {tags.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {tags.map((tag) => (
                          <span key={tag} className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${tagColor(tag)}`}>
                            <Tag className="h-2.5 w-2.5" />{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* POS-I106: portal status at a glance */}
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {c.portalEnabled === false
                        ? t('portalOff')
                        : c.portalLastLoginAt
                          ? t('portalLastSignIn', { date: formatDate(c.portalLastLoginAt) })
                          : c.portalLinkSentAt
                            ? t('portalLinkSent', { date: formatDate(c.portalLinkSentAt) })
                            : t('portalNeverUsed')}
                    </div>
                    {(c.marketingSmsOptIn || c.marketingEmailOptIn) && (
                      <div className="mt-1 flex gap-1">
                        {c.marketingSmsOptIn && (
                          <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary-text">
                            📱 SMS
                          </span>
                        )}
                        {c.marketingEmailOptIn && (
                          <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary-text">
                            ✉️ Email
                          </span>
                        )}
                      </div>
                    )}
                    {c.referralCode && (
                      <button
                        type="button"
                        title="Copy referral code"
                        className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-info/10 px-2 py-0.5 text-xs font-mono font-medium text-info-text hover:bg-info/20 transition-colors"
                        onClick={() => { navigator.clipboard.writeText(c.referralCode ?? ''); toast.success('Referral code copied!'); }}
                      >
                        <Share2 className="h-2.5 w-2.5" />
                        {c.referralCode}
                      </button>
                    )}
                  </div>
                  <TableRowActions
                    actions={[
                      {
                        key: 'edit',
                        label: t('editCustomerTooltip'),
                        icon: Pencil,
                        onSelect: () => openEditDialog(c),
                        permission: canWrite,
                        primary: true,
                      },
                      {
                        key: 'ledger',
                        label: t('viewLedgerTooltip'),
                        icon: FileText,
                        onSelect: () => navigate(`/customers/${c.id}/ledger`),
                        primary: true,
                      },
                      {
                        key: 'whatsapp',
                        label: t('sharePortalWhatsApp'),
                        icon: MessageCircle,
                        onSelect: () => {
                          // POS-I106: staff can see that (and when) the link was sent.
                          api.post(`/customers/${c.id}/portal-link-sent`).catch(() => undefined);
                          window.open(
                            `https://wa.me/${toWhatsAppNumber(c.phone)}?text=${encodeURIComponent(t('whatsAppMessage', { name: c.name, url: `${window.location.origin}/portal/login?tenant=${tenant?.slug}` }))}`,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        },
                        permission: !!c.phone,
                      },
                      {
                        key: 'delete',
                        label: t('deleteCustomerTooltip'),
                        icon: Trash2,
                        onSelect: () => setConfirmDelete(c.id),
                        permission: canDelete,
                        variant: 'destructive',
                      },
                    ]}
                  />
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {(data?.total ?? data?.meta?.total ?? 0) > 0 && (
        <PaginationBar
          page={page}
          pageSize={20}
          total={data?.total ?? data?.meta?.total ?? 0}
          onPageChange={setPage}
        />
      )}

      {/* Add/Edit Customer Dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header={editingId ? t('editCustomer') : t('addCustomer')}
        footer={customerDialogFooter}
        style={{ width: '52rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
      >
        <form id="customer-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('name')} *</Label>
            <Input id="name" value={form.name} onChange={(e) => updateField('name', e.target.value)} placeholder={t('customerNamePlaceholder')} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">{t('phone')}</Label>
              <Input id="phone" dir="ltr" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder={t('phonePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} placeholder={t('emailPlaceholder')} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">{t('address')}</Label>
            <Input id="address" value={form.address} onChange={(e) => updateField('address', e.target.value)} placeholder={t('addressPlaceholder')} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">{t('provinceCity')}</Label>
              <Input id="city" value={form.city} onChange={(e) => updateField('city', e.target.value)} placeholder={t('provincePlaceholder')} />
              {canViewFbr && <p className="text-xs text-muted-foreground">{t('provinceHelpText')}</p>}
            </div>
            {canViewFbr && (
              <div className="space-y-2">
                <Label htmlFor="ntn">{t('ntnTaxNumber')}</Label>
                <Input id="ntn" dir="ltr" value={form.ntn} onChange={(e) => updateField('ntn', e.target.value)} placeholder={t('ntnPlaceholder')} />
              </div>
            )}
          </div>
          {canViewFbr && (
            <div className="space-y-2">
              <Label htmlFor="cnic">{t('cnic')}</Label>
              <Input id="cnic" dir="ltr" value={form.cnic} onChange={(e) => updateField('cnic', e.target.value)} placeholder={t('cnicPlaceholder')} />
            </div>
          )}
          {canViewFbr && (
            <div className="rounded-lg border p-3 space-y-1.5">
              <Label className="text-sm font-medium">{t('atlStatusLabel')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('atlStatusHelp')}
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, isOnAtl: false }))}
                  className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${!form.isOnAtl ? 'bg-warning/10 border-warning/30 text-warning-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                >
                  <UserX className="h-3 w-3" /> {t('nonAtlOption')}
                </button>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, isOnAtl: true }))}
                  className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${form.isOnAtl ? 'bg-success/10 border-success/30 text-success-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                >
                  <UserCheck className="h-3 w-3" /> {t('atlOption')}
                </button>
              </div>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="creditLimit">{t('creditLimitPkr')}</Label>
            <Input
              id="creditLimit"
              type="number"
              min="0"
              step="0.01"
              placeholder={t('creditLimitPlaceholder')}
              value={form.creditLimit ?? ''}
              onChange={(e) => setForm({ ...form, creditLimit: e.target.value ? Number(e.target.value) : undefined })}
            />
            <p className="text-xs text-muted-foreground">{t('creditLimitHelp')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">{t('notes')}</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder={t('notesPlaceholder')}
              rows={3}
            />
          </div>

          {/* MEIMS SRS Module 1 — Personal details */}
          <details className="rounded-lg border">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">Personal Details</summary>
            <div className="space-y-4 border-t p-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fatherName">Father / Husband Name</Label>
                  <Input id="fatherName" value={form.fatherName} onChange={(e) => updateField('fatherName', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input id="dateOfBirth" type="date" value={form.dateOfBirth} onChange={(e) => updateField('dateOfBirth', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Dropdown
                    inputId="gender"
                    className="w-full"
                    value={form.gender}
                    onChange={(e) => updateField('gender', e.value)}
                    options={[
                      { label: 'Not specified', value: '' },
                      { label: 'Male', value: 'MALE' },
                      { label: 'Female', value: 'FEMALE' },
                      { label: 'Other', value: 'OTHER' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Not specified"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="alternatePhone">Alternate Phone</Label>
                  <Input id="alternatePhone" value={form.alternatePhone} onChange={(e) => updateField('alternatePhone', e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="province">Province</Label>
                <Input id="province" value={form.province} onChange={(e) => updateField('province', e.target.value)} />
              </div>
            </div>
          </details>

          {/* MEIMS SRS Module 1 — Military / service information */}
          <details className="rounded-lg border">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">Military / Service Information</summary>
            <div className="space-y-4 border-t p-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="armyNumber">Army Number</Label>
                  <Input id="armyNumber" value={form.armyNumber} onChange={(e) => updateField('armyNumber', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rank">Rank</Label>
                  <Input id="rank" value={form.rank} onChange={(e) => updateField('rank', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Input id="unit" value={form.unit} onChange={(e) => updateField('unit', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brigade">Brigade / Formation</Label>
                  <Input id="brigade" value={form.brigade} onChange={(e) => updateField('brigade', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postingStation">Posting Station</Label>
                  <Input id="postingStation" value={form.postingStation} onChange={(e) => updateField('postingStation', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serviceStatus">Service Status</Label>
                  <Dropdown
                    inputId="serviceStatus"
                    className="w-full"
                    value={form.serviceStatus}
                    onChange={(e) => updateField('serviceStatus', e.value)}
                    options={[
                      { label: 'Not specified', value: '' },
                      { label: 'Active', value: 'ACTIVE' },
                      { label: 'Retired', value: 'RETIRED' },
                      { label: 'Reserve', value: 'RESERVE' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Not specified"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="serviceYears">Years of Service</Label>
                <Input id="serviceYears" type="number" min="0" value={form.serviceYears ?? ''} onChange={(e) => setForm({ ...form, serviceYears: e.target.value ? Number(e.target.value) : undefined })} />
              </div>
            </div>
          </details>

          {/* MEIMS SRS Module 1 — Employment information */}
          <details className="rounded-lg border">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">Employment Information</summary>
            <div className="space-y-4 border-t p-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employerName">Employer Name</Label>
                  <Input id="employerName" value={form.employerName} onChange={(e) => updateField('employerName', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input id="department" value={form.department} onChange={(e) => updateField('department', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="monthlySalary">Monthly Salary (PKR)</Label>
                  <Input id="monthlySalary" type="number" min="0" step="0.01" value={form.monthlySalary ?? ''} onChange={(e) => setForm({ ...form, monthlySalary: e.target.value ? Number(e.target.value) : undefined })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employmentDuration">Employment Duration</Label>
                  <Input id="employmentDuration" value={form.employmentDuration} onChange={(e) => updateField('employmentDuration', e.target.value)} placeholder="e.g. 5 years" />
                </div>
              </div>
            </div>
          </details>
          {editingId && <CustomerDocumentsManager customerId={editingId} canWrite={canWrite} />}
          {editingId && <CustomerGuarantorsManager customerId={editingId} canWrite={canWrite} canDelete={canDelete} />}
          <div className="space-y-2">
            <Label htmlFor="tagsInput">{t('tags')}</Label>
            <Input
              id="tagsInput"
              value={form.tagsInput}
              onChange={(e) => updateField('tagsInput', e.target.value)}
              placeholder={t('tagsPlaceholder')}
            />
            <p className="text-xs text-muted-foreground">{t('tagsHelp')}</p>
            {form.tagsInput && (
              <div className="flex flex-wrap gap-1 mt-1">
                {form.tagsInput.split(',').map(t => t.trim()).filter(Boolean).map((tag) => (
                  <span key={tag} className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${tagColor(tag)}`}>
                    <Tag className="h-2.5 w-2.5" />{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          {canUsePriceTiers && priceTiersData && priceTiersData.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="priceTierId">{t('priceTier')}</Label>
              <Dropdown
                inputId="priceTierId"
                value={form.priceTierId ?? ''}
                onChange={(e) => updateField('priceTierId', e.value)}
                options={[
                  { label: t('noPriceTier'), value: '' },
                  ...priceTiersData.filter((tier) => tier.isActive).map((tier) => ({
                    label: `${tier.name}${tier.description ? ` — ${tier.description}` : ''}`,
                    value: tier.id,
                  })),
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('noPriceTier')}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">{t('priceTierHelp')}</p>
            </div>
          )}

          {/* Marketing Opt-Ins */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Marketing Consent</Label>
            <div className="flex flex-col gap-2">
              <Label className="flex items-center gap-2 cursor-pointer select-none font-normal leading-normal text-foreground">
                <Checkbox
                  checked={form.marketingSmsOptIn}
                  onChange={(e) => updateField('marketingSmsOptIn', e.target.checked)}
                />
                <span className="text-sm">SMS marketing opt-in</span>
                <span className="text-xs text-muted-foreground">(customer consents to promotional SMS)</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer select-none font-normal leading-normal text-foreground">
                <Checkbox
                  checked={form.marketingEmailOptIn}
                  onChange={(e) => updateField('marketingEmailOptIn', e.target.checked)}
                />
                <span className="text-sm">Email marketing opt-in</span>
                <span className="text-xs text-muted-foreground">(customer consents to promotional emails)</span>
              </Label>
            </div>
          </div>

          {/* POS-I106: customer portal access */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('portalAccessTitle')}</Label>
            <Label className="flex items-center gap-2 cursor-pointer select-none font-normal leading-normal text-foreground">
              <Checkbox
                checked={form.portalEnabled}
                onChange={(e) => updateField('portalEnabled', e.target.checked)}
              />
              <span className="text-sm">{t('portalAccessAllowed')}</span>
            </Label>
            <p className="text-xs text-muted-foreground">{t('portalAccessHint')}</p>
          </div>

          {/* Referral */}
          {!editingId && (
            <div className="space-y-2">
              <Label htmlFor="referredByCode" className="flex items-center gap-1.5">
                <Share2 className="h-3.5 w-3.5 text-info-text" /> Referred By Code
              </Label>
              <Input
                id="referredByCode"
                value={form.referredByCode}
                onChange={(e) => updateField('referredByCode', e.target.value.toUpperCase())}
                placeholder="e.g. AHME-X4T2"
                className="font-mono uppercase"
              />
              <p className="text-xs text-muted-foreground">If this customer was referred by someone, enter their referral code. Both parties will receive loyalty bonus points.</p>
            </div>
          )}
        </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => deleteMutation.mutate(confirmDelete!)}
        title={t('deleteCustomer')}
        message={t('deleteCustomerMessage')}
        confirmLabel={t('deleteLabel')}
        variant="danger"
      />
    </div>
  );
}

const DOCUMENT_TYPES = [
  'CNIC_FRONT', 'CNIC_BACK', 'ARMY_CARD_FRONT', 'ARMY_CARD_BACK',
  'PASSPORT_PHOTO', 'UTILITY_BILL', 'SALARY_SLIP', 'SERVICE_CERTIFICATE', 'OTHER',
];

const DOC_ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,application/pdf';
const DOC_MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

async function uploadDocumentFile(file: File, folder: string): Promise<{ key: string; fileName: string; mimeType: string; fileSize: number }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await api.post('/uploads', fd, {
    params: { folder },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

function CustomerDocumentsManager({ customerId, canWrite }: { customerId: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [documentType, setDocumentType] = useState('CNIC_FRONT');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<string | null>(null);

  const { data: documents, isLoading } = useQuery<DocumentRecord[]>({
    queryKey: ['customer-documents', customerId],
    queryFn: () => api.get(`/customers/${customerId}/documents`).then((r) => r.data),
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const uploaded = await uploadDocumentFile(selectedFile!, 'customer-documents');
      return api.post(`/customers/${customerId}/documents`, {
        documentType,
        fileUrl: uploaded.key,
        storageKey: uploaded.key,
        fileName: uploaded.fileName,
        mimeType: uploaded.mimeType,
        fileSize: uploaded.fileSize,
      });
    },
    onSuccess: () => {
      toast.success('Document attached');
      queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
      setSelectedFile(null);
    },
    onError: (err) => notify.error(err, 'Failed to attach document'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${customerId}/documents/${id}`),
    onSuccess: () => {
      toast.success('Document deleted');
      queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
      setConfirmDeleteDoc(null);
    },
    onError: (err) => notify.error(err, 'Failed to delete document'),
  });

  const handleAdd = () => {
    if (!selectedFile) {
      toast.error('Please choose a file to upload');
      return;
    }
    addMutation.mutate();
  };

  return (
    <details className="rounded-lg border">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">Documents</summary>
      <div className="space-y-3 border-t p-3">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading documents…</p>
        ) : documents?.length ? (
          <ul className="space-y-2">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <button type="button" onClick={() => openStoredObject(doc.fileUrl)} className="flex items-center gap-2 text-primary-text hover:underline">
                  <FileText className="h-4 w-4" />
                  <span>{doc.documentType.replace(/_/g, ' ')}{doc.fileName ? ` — ${doc.fileName}` : ''}</span>
                </button>
                {canWrite && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDeleteDoc(doc.id)}>
                    <Trash2 className="h-4 w-4 text-destructive-text" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No documents attached yet.</p>
        )}

        {canWrite && (
          <div className="space-y-2 border-t pt-3">
            <div className="space-y-1">
              <Label htmlFor="docType">Document Type</Label>
              <Dropdown
                inputId="docType"
                className="w-full"
                value={documentType}
                onChange={(e) => setDocumentType(e.value)}
                options={DOCUMENT_TYPES.map((dt) => ({ label: dt.replace(/_/g, ' '), value: dt }))}
                optionLabel="label"
                optionValue="value"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="docFile">File (JPG, PNG, or PDF — max 20 MB)</Label>
              <input
                id="docFile"
                type="file"
                accept={DOC_ACCEPTED_FILE_TYPES}
                className="block w-full text-sm text-foreground file:me-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:text-primary-foreground hover:file:cursor-pointer"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  if (f && f.size > DOC_MAX_FILE_SIZE) {
                    toast.error('File exceeds the 20 MB limit.');
                    e.target.value = '';
                    setSelectedFile(null);
                    return;
                  }
                  setSelectedFile(f);
                }}
              />
              {selectedFile && <p className="text-xs text-muted-foreground">{selectedFile.name}</p>}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={addMutation.isPending}>
              <Plus className="h-4 w-4 me-1" /> {addMutation.isPending ? 'Uploading…' : 'Attach Document'}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDeleteDoc}
        onCancel={() => setConfirmDeleteDoc(null)}
        onConfirm={() => deleteMutation.mutate(confirmDeleteDoc!)}
        title="Delete Document"
        message="Are you sure you want to delete this document? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </details>
  );
}

const GUARANTOR_DOCUMENT_TYPES = ['CNIC_FRONT', 'CNIC_BACK', 'ARMY_CARD', 'UTILITY_BILL', 'OTHER'];

const emptyGuarantor = { name: '', fatherName: '', cnic: '', phone: '', alternatePhone: '', address: '', relationship: '', notes: '' };

function CustomerGuarantorsManager({ customerId, canWrite, canDelete }: { customerId: string; canWrite: boolean; canDelete: boolean }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyGuarantor);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data: guarantors, isLoading } = useQuery<GuarantorRecord[]>({
    queryKey: ['customer-guarantors', customerId],
    queryFn: () => api.get(`/customers/${customerId}/guarantors`).then((r) => r.data),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['customer-guarantors', customerId] });

  const saveMutation = useMutation({
    mutationFn: () =>
      editingId
        ? api.patch(`/guarantors/${editingId}`, form)
        : api.post(`/customers/${customerId}/guarantors`, form),
    onSuccess: () => {
      toast.success(editingId ? 'Guarantor updated' : 'Guarantor added');
      invalidate();
      setForm(emptyGuarantor);
      setEditingId(null);
      setShowForm(false);
    },
    onError: (err) => notify.error(err, 'Failed to save guarantor'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/guarantors/${id}`),
    onSuccess: () => {
      toast.success('Guarantor deleted');
      invalidate();
      setConfirmDelete(null);
    },
    onError: (err) => notify.error(err, 'Failed to delete guarantor'),
  });

  const startEdit = (g: GuarantorRecord) => {
    setForm({
      name: g.name || '', fatherName: g.fatherName || '', cnic: g.cnic || '', phone: g.phone || '',
      alternatePhone: g.alternatePhone || '', address: g.address || '', relationship: g.relationship || '', notes: g.notes || '',
    });
    setEditingId(g.id);
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) { toast.error('Guarantor name is required'); return; }
    saveMutation.mutate();
  };

  const updateField = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <details className="rounded-lg border">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">Guarantors</summary>
      <div className="space-y-3 border-t p-3">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading guarantors…</p>
        ) : guarantors?.length ? (
          <ul className="space-y-2">
            {guarantors.map((g) => (
              <li key={g.id} className="rounded-md border p-3 text-sm space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{g.name}{g.relationship ? ` (${g.relationship})` : ''}</p>
                    <p className="text-xs text-muted-foreground">
                      {[g.cnic, g.phone].filter(Boolean).join(' · ') || 'No contact details'}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {canWrite && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(g)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDelete(g.id)}>
                        <Trash2 className="h-4 w-4 text-destructive-text" />
                      </Button>
                    )}
                  </div>
                </div>
                <GuarantorDocumentsManager guarantorId={g.id} canWrite={canWrite} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No guarantors added yet.</p>
        )}

        {canWrite && !showForm && (
          <Button type="button" variant="outline" size="sm" onClick={() => { setForm(emptyGuarantor); setEditingId(null); setShowForm(true); }}>
            <Plus className="h-4 w-4 me-1" /> Add Guarantor
          </Button>
        )}

        {canWrite && showForm && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="gName">Name *</Label>
                <Input id="gName" value={form.name} onChange={(e) => updateField('name', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="gFather">Father / Husband Name</Label>
                <Input id="gFather" value={form.fatherName} onChange={(e) => updateField('fatherName', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="gCnic">CNIC</Label>
                <Input id="gCnic" dir="ltr" value={form.cnic} onChange={(e) => updateField('cnic', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="gRel">Relationship</Label>
                <Input id="gRel" value={form.relationship} onChange={(e) => updateField('relationship', e.target.value)} placeholder="e.g. Brother" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="gPhone">Phone</Label>
                <Input id="gPhone" dir="ltr" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="gAltPhone">Alternate Phone</Label>
                <Input id="gAltPhone" value={form.alternatePhone} onChange={(e) => updateField('alternatePhone', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="gAddress">Address</Label>
              <Input id="gAddress" value={form.address} onChange={(e) => updateField('address', e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
              <Button type="button" variant="gradient" size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
                {editingId ? 'Update Guarantor' : 'Save Guarantor'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => deleteMutation.mutate(confirmDelete!)}
        title="Delete Guarantor"
        message="Are you sure you want to delete this guarantor? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </details>
  );
}

function GuarantorDocumentsManager({ guarantorId, canWrite }: { guarantorId: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [documentType, setDocumentType] = useState('CNIC_FRONT');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<string | null>(null);

  const { data: documents } = useQuery<DocumentRecord[]>({
    queryKey: ['guarantor-documents', guarantorId],
    queryFn: () => api.get(`/guarantors/${guarantorId}/documents`).then((r) => r.data),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['guarantor-documents', guarantorId] });

  const addMutation = useMutation({
    mutationFn: async () => {
      const uploaded = await uploadDocumentFile(selectedFile!, 'guarantor-documents');
      return api.post(`/guarantors/${guarantorId}/documents`, {
        documentType,
        fileUrl: uploaded.key,
        storageKey: uploaded.key,
        fileName: uploaded.fileName,
        mimeType: uploaded.mimeType,
        fileSize: uploaded.fileSize,
      });
    },
    onSuccess: () => {
      toast.success('Document attached');
      invalidate();
      setSelectedFile(null);
    },
    onError: (err) => notify.error(err, 'Failed to attach document'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/guarantors/${guarantorId}/documents/${id}`),
    onSuccess: () => {
      toast.success('Document deleted');
      invalidate();
      setConfirmDeleteDoc(null);
    },
    onError: (err) => notify.error(err, 'Failed to delete document'),
  });

  const handleAdd = () => {
    if (!selectedFile) { toast.error('Please choose a file to upload'); return; }
    addMutation.mutate();
  };

  return (
    <details className="rounded-md border">
      <summary className="cursor-pointer select-none px-2 py-1.5 text-xs font-medium">Guarantor Documents</summary>
      <div className="space-y-2 border-t p-2">
        {documents?.length ? (
          <ul className="space-y-1">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between rounded border px-2 py-1 text-xs">
                <button type="button" onClick={() => openStoredObject(doc.fileUrl)} className="flex items-center gap-1.5 text-primary-text hover:underline">
                  <FileText className="h-3.5 w-3.5" />
                  <span>{doc.documentType.replace(/_/g, ' ')}{doc.fileName ? ` — ${doc.fileName}` : ''}</span>
                </button>
                {canWrite && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDeleteDoc(doc.id)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive-text" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No documents.</p>
        )}
        {canWrite && (
          <div className="space-y-2">
            <Dropdown
              className="w-full text-xs"
              value={documentType}
              onChange={(e) => setDocumentType(e.value)}
              options={GUARANTOR_DOCUMENT_TYPES.map((dt) => ({ label: dt.replace(/_/g, ' '), value: dt }))}
              optionLabel="label"
              optionValue="value"
            />
            <input
              type="file"
              accept={DOC_ACCEPTED_FILE_TYPES}
              className="block w-full text-xs text-foreground file:me-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-1 file:text-xs file:text-primary-foreground hover:file:cursor-pointer"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (f && f.size > DOC_MAX_FILE_SIZE) {
                  toast.error('File exceeds the 20 MB limit.');
                  e.target.value = '';
                  setSelectedFile(null);
                  return;
                }
                setSelectedFile(f);
              }}
            />
            {selectedFile && <p className="text-xs text-muted-foreground">{selectedFile.name}</p>}
            <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={addMutation.isPending}>
              <Plus className="h-3.5 w-3.5 me-1" /> {addMutation.isPending ? 'Uploading…' : 'Attach'}
            </Button>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!confirmDeleteDoc}
        onCancel={() => setConfirmDeleteDoc(null)}
        onConfirm={() => deleteMutation.mutate(confirmDeleteDoc!)}
        title="Delete Document"
        message="Are you sure you want to delete this document?"
        confirmLabel="Delete"
        variant="danger"
      />
    </details>
  );
}
